import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { BookProgress } from '@lyceo/lyceo-pupil-app/practise/util-models';
import { ScopeActions } from '../scope/scope.actions';
import { BookProgressActions } from './book-progress.actions';
import { CallState, LoadingState } from '@lyceo/shared/util-loading';
import {
  mapBookProgress,
  mapPersonUsesBookToBookProgress,
  mapScopesToBookProgress,
} from './book-progress.functions';
import { PersonUsesBookActions } from '../person-uses-book/person-uses-book.actions';
import { AuthActions } from '@lyceo/shared/auth/data-access';

export const BOOK_PROGRESS_FEATURE_KEY: string = 'bookProgress';

export interface BookProgressState extends EntityState<BookProgress> {
  fullyLoadedIds: string[];
  tocCallState: CallState;
}

export const bookProgressAdapter: EntityAdapter<BookProgress> =
  createEntityAdapter<BookProgress>();

export const initialBookProgressState: BookProgressState =
  bookProgressAdapter.getInitialState({
    fullyLoadedIds: [],
    tocCallState: LoadingState.INIT,
  });

const reducer: ActionReducer<BookProgressState, Action> = createReducer(
  initialBookProgressState,
  on(
    AuthActions.logoutSuccess,
    (): BookProgressState => initialBookProgressState,
  ),
  on(
    ScopeActions.getPersonScopesSuccess,
    (state, action): BookProgressState =>
      bookProgressAdapter.upsertMany(mapScopesToBookProgress(action.scopes), {
        ...state,
      }),
  ),
  on(
    PersonUsesBookActions.getLatestPersonUsesBooksSuccess,
    (state, action): BookProgressState =>
      bookProgressAdapter.upsertMany(
        mapPersonUsesBookToBookProgress(action.personUsesBooks),
        {
          ...state,
        },
      ),
  ),
  on(
    BookProgressActions.getBookProgress,
    (state): BookProgressState => ({
      ...state,
      tocCallState: LoadingState.LOADING,
    }),
  ),
  on(
    BookProgressActions.getBookProgressSuccess,
    (state, action): BookProgressState => {
      if (action.bookProgressResponse.bookProgress) {
        return bookProgressAdapter.upsertOne(
          mapBookProgress(action.bookProgressResponse.bookProgress),
          {
            ...state,
            fullyLoadedIds: [
              ...state.fullyLoadedIds,
              action.bookProgressResponse.bookProgress.bookId,
            ],
            tocCallState: LoadingState.LOADED,
          },
        );
      }
      return {
        ...state,
        tocCallState: LoadingState.LOADED,
      };
    },
  ),
  on(
    BookProgressActions.getBookProgressFailure,
    (state, { error }): BookProgressState => ({
      ...state,
      tocCallState: { errorMsg: error.message },
    }),
  ),
  on(
    BookProgressActions.updateBookProgressSuccess,
    (state, action): BookProgressState => {
      if (action.bookProgressResponse.bookProgress) {
        return bookProgressAdapter.upsertOne(
          mapBookProgress(action.bookProgressResponse.bookProgress),
          {
            ...state,
          },
        );
      }
      return {
        ...state,
      };
    },
  ),
  on(ScopeActions.switchBookSuccess, (state, action): BookProgressState => {
    if (action.scope.personUsesBook?.bookProgress) {
      return bookProgressAdapter.upsertOne(
        mapBookProgress(action.scope.personUsesBook.bookProgress),
        {
          ...state,
        },
      );
    }
    return state;
  }),
  on(ScopeActions.switchBooksSuccess, (state, action): BookProgressState => {
    return bookProgressAdapter.upsertMany(
      action.scopes.reduce((booksProgresses: BookProgress[], scopeDto) => {
        if (scopeDto.personUsesBook?.bookProgress) {
          booksProgresses.push(
            mapBookProgress(scopeDto.personUsesBook.bookProgress),
          );
        }
        return booksProgresses;
      }, []),
      {
        ...state,
      },
    );
  }),
);

export function bookProgressReducer(
  state: BookProgressState | undefined,
  action: Action,
): BookProgressState {
  return reducer(state, action);
}
